import { RefObject, useEffect, useRef } from "react";

interface UseInfiniteScrollOptions<T extends HTMLElement> {
  /** Ref to the scrollable container (or parent when using `getScrollElement`). */
  containerRef: RefObject<T | null>;
  /** Whether more pages exist. Detaches the listener when `false`. */
  hasMore: boolean;
  /** `true` while a fetch is in flight; resets the in-flight guard when it turns `false`. */
  isLoading: boolean;
  /** Called once when the user scrolls within `threshold` px of the bottom. Must be stable (`useCallback`). */
  onLoadMore: () => void;
  /** Distance from the bottom (px) that triggers `onLoadMore`. Defaults to 100. */
  threshold?: number;
  /** Resolves the actual scrollable element inside the container (e.g. `.k-list-content`). */
  getScrollElement?: (container: T) => HTMLElement | null;
}

const DEFAULT_THRESHOLD = 100;

/**
 * Fires `onLoadMore` when the user scrolls within `threshold` px of the bottom.
 * Blocks concurrent triggers with an imperative ref; resets when `isLoading` turns `false`.
 * Pair with {@link useInfinitePageItems} to accumulate pages.
 */
const useInfiniteScroll = <T extends HTMLElement>({
  containerRef,
  hasMore,
  isLoading,
  onLoadMore,
  threshold = DEFAULT_THRESHOLD,
  getScrollElement,
}: UseInfiniteScrollOptions<T>) => {
  const isFetchingRef = useRef(false);

  useEffect(() => {
    if (!isLoading) {
      isFetchingRef.current = false;
    }
  }, [isLoading]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !hasMore) return;

    const scrollElement = getScrollElement?.(container) ?? container;
    if (!scrollElement) return;

    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollElement;

      if (
        scrollHeight - scrollTop - clientHeight < threshold &&
        !isFetchingRef.current &&
        hasMore &&
        !isLoading
      ) {
        isFetchingRef.current = true;
        onLoadMore();
      }
    };

    scrollElement.addEventListener("scroll", onScroll);
    return () => scrollElement.removeEventListener("scroll", onScroll);
  }, [
    containerRef,
    getScrollElement,
    hasMore,
    isLoading,
    onLoadMore,
    threshold,
  ]);
};

export default useInfiniteScroll;
